import { useEffect, useMemo, useState } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import {
  Activity,
  Calendar,
  CheckCircle2,
  Clock,
  Database,
  Download,
  Eye,
  FileText,
  Fingerprint,
  Globe2,
  Info,
  Laptop,
  Layers3,
  RefreshCw,
  Search,
  ShieldCheck,
  User,
  XCircle,
} from "lucide-react";

import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { AppDataTable } from "@/components/ui/app-data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  type AuditEvent,
  type RawActivityLog,
  formatAuditDate,
  formatAuditTime,
  formatAuditTimestamp,
  toAuditEvent,
  uniqueSorted,
} from "@/lib/audit-trail";
import { cn } from "@/lib/utils";
import { addDays, format, subDays } from "date-fns";

const OUTCOME_STYLES: Record<AuditEvent["outcome"], string> = {
  Success: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  Warning: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  Failure: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  Info: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
};

const MODULE_STYLES: Record<string, string> = {
  Policy: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  Governance: "border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300",
  Compliance: "border-lime-500/30 bg-lime-500/10 text-lime-700 dark:text-lime-300",
  Risk: "border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-300",
  "Knowledge Base": "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300",
  "AI Agent": "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300",
  "Team & Admin": "border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-300",
  Billing: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  Quizzes: "border-indigo-500/30 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300",
};

const outcomeIconMap = {
  Success: CheckCircle2,
  Warning: Info,
  Failure: XCircle,
  Info: Activity,
} satisfies Record<AuditEvent["outcome"], typeof Activity>;

const ActivityLog = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date } | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [logs, setLogs] = useState<RawActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [users, setUsers] = useState<{ id: string; name: string; email: string }[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);

  const { profile } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (profile?.company_id) {
      fetchActivityLogs();
      fetchUsers();
    }
  }, [profile?.company_id]);

  useEffect(() => {
    if (profile?.company_id) {
      fetchActivityLogs();
    }
  }, [userFilter, dateRange, profile?.company_id]);

  const fetchActivityLogs = async (isRefresh = false) => {
    if (!profile?.company_id) return;

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      let query = supabase
        .from("activity_logs")
        .select(`
          *,
          user:profiles(id, name, email)
        `)
        .eq("company_id", profile.company_id)
        .order("created_at", { ascending: false });

      if (userFilter !== "all") {
        query = query.eq("user_id", userFilter);
      }

      if (dateRange?.from) {
        query = query.gte("created_at", dateRange.from.toISOString());
      }

      if (dateRange?.to) {
        query = query.lte("created_at", addDays(dateRange.to, 1).toISOString());
      }

      const { data: logsData, error: logsError } = await query;

      if (logsError) throw logsError;

      setLogs((logsData || []) as RawActivityLog[]);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
      toast({
        title: "Error",
        description: "Failed to fetch audit trail entries",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchUsers = async () => {
    if (!profile?.company_id) return;

    try {
      const { data: usersData, error: usersError } = await supabase
        .from("profiles")
        .select("id, name, email")
        .eq("company_id", profile.company_id)
        .order("name");

      if (usersError) throw usersError;
      setUsers(usersData || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const auditEvents = useMemo(() => logs.map(toAuditEvent), [logs]);

  const filterOptions = useMemo(() => {
    return {
      modules: uniqueSorted(auditEvents.map((event) => event.module)),
      categories: uniqueSorted(auditEvents.map((event) => event.eventCategory)),
      outcomes: uniqueSorted(auditEvents.map((event) => event.outcome)),
    };
  }, [auditEvents]);

  const filteredEvents = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return auditEvents.filter((event) => {
      const matchesSearch =
        !normalizedSearch ||
        [
          event.actorName,
          event.actorEmail,
          event.module,
          event.eventName,
          event.eventCategory,
          event.targetType,
          event.targetName,
          event.summary,
          event.statusChange,
          event.ipAddress,
          event.targetId,
          event.id,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch));

      return (
        matchesSearch &&
        (moduleFilter === "all" || event.module === moduleFilter) &&
        (categoryFilter === "all" || event.eventCategory === categoryFilter) &&
        (outcomeFilter === "all" || event.outcome === outcomeFilter)
      );
    });
  }, [auditEvents, categoryFilter, moduleFilter, outcomeFilter, searchTerm]);

  const csvEscape = (value: unknown) => {
    const text = value === null || value === undefined || value === "" ? "N/A" : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const exportToCSV = () => {
    const headers = [
      "Timestamp",
      "Actor Name",
      "Actor Email",
      "Actor Type",
      "Module",
      "Event",
      "Category",
      "Outcome",
      "Target Type",
      "Target Name",
      "Target ID",
      "Summary",
      "Status Change",
      "Field Changes",
      "Source",
      "IP Address",
      "Device",
      "Event ID",
      "Raw Details",
    ];

    const csvData = filteredEvents.map((event) => [
      formatAuditTimestamp(event.timestamp),
      event.actorName,
      event.actorEmail,
      event.actorType,
      event.module,
      event.eventName,
      event.eventCategory,
      event.outcome,
      event.targetType,
      event.targetName,
      event.targetId,
      event.summary,
      event.statusChange,
      event.fieldChanges.map((change) => `${change.field}: ${change.from} -> ${change.to}`).join(" | "),
      event.source,
      event.ipAddress,
      event.device,
      event.id,
      event.raw.details ? JSON.stringify(event.raw.details) : null,
    ]);

    const csvContent = [headers, ...csvData]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `audit_trail_${format(new Date(), "yyyy-MM-dd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export successful",
      description: "Audit trail has been exported to CSV",
    });
  };

  const auditColumns = useMemo<ColumnDef<AuditEvent>[]>(() => [
    {
      accessorKey: "timestamp",
      header: "Time",
      cell: ({ row }) => (
        <div className="flex min-w-[128px] items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="font-medium text-foreground">{formatAuditDate(row.original.timestamp)}</div>
            <div className="text-xs text-muted-foreground">{formatAuditTime(row.original.timestamp)}</div>
          </div>
        </div>
      ),
      meta: { headerClassName: "w-[150px]" },
    },
    {
      id: "actor",
      header: "Actor",
      cell: ({ row }) => (
        <div className="flex min-w-[190px] items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <div className="min-w-0">
            <div className="truncate font-medium">{row.original.actorName}</div>
            <div className="truncate text-xs text-muted-foreground">
              {row.original.actorEmail || row.original.actorType}
            </div>
          </div>
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "module",
      header: "Module",
      cell: ({ row }) => (
        <Badge variant="outline" className={cn("whitespace-nowrap", MODULE_STYLES[row.original.module])}>
          {row.original.module}
        </Badge>
      ),
    },
    {
      accessorKey: "eventName",
      header: "Event",
      cell: ({ row }) => {
        const OutcomeIcon = outcomeIconMap[row.original.outcome];

        return (
          <div className="min-w-[220px] space-y-1">
            <div className="flex items-center gap-2">
              <OutcomeIcon className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{row.original.eventName}</span>
            </div>
            <div className="text-xs text-muted-foreground">{row.original.summary}</div>
          </div>
        );
      },
    },
    {
      accessorKey: "targetName",
      header: "Target",
      cell: ({ row }) => (
        <div className="min-w-[190px]">
          <div className="font-medium">{row.original.targetName}</div>
          <div className="text-xs text-muted-foreground">{row.original.targetType}</div>
        </div>
      ),
    },
    {
      id: "change",
      header: "Change",
      cell: ({ row }) => (
        <div className="min-w-[210px] space-y-1">
          {row.original.statusChange ? (
            <div className="rounded-md border bg-muted/40 px-2 py-1 text-xs font-medium">
              {row.original.statusChange}
            </div>
          ) : row.original.fieldChanges.length > 0 ? (
            row.original.fieldChanges.slice(0, 2).map((change) => (
              <div key={`${change.field}-${change.to}`} className="text-xs">
                <span className="font-medium">{change.field}:</span>{" "}
                <span className="text-muted-foreground">{change.to}</span>
              </div>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">No field-level change</span>
          )}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "outcome",
      header: "Outcome",
      cell: ({ row }) => (
        <Badge variant="outline" className={cn("whitespace-nowrap", OUTCOME_STYLES[row.original.outcome])}>
          {row.original.outcome}
        </Badge>
      ),
    },
    {
      id: "source",
      header: "Source / Network",
      cell: ({ row }) => (
        <div className="min-w-[180px] space-y-1 text-sm">
          <div className="flex items-center gap-2">
            <Laptop className="h-4 w-4 text-muted-foreground" />
            <span>{row.original.device || row.original.source}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Globe2 className="h-3.5 w-3.5" />
            <span>{row.original.ipAddress || "N/A"}</span>
          </div>
        </div>
      ),
      enableSorting: false,
    },
    {
      id: "evidence",
      header: "Evidence",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          className="gap-2"
          onClick={(event) => {
            event.stopPropagation();
            setSelectedEvent(row.original);
          }}
        >
          <Eye className="h-4 w-4" />
          View
        </Button>
      ),
      enableSorting: false,
    },
  ], []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton variant="shimmer" className="h-8 w-44" />
            <Skeleton variant="shimmer" className="h-5 w-80" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton variant="shimmer" className="h-9 w-24" />
            <Skeleton variant="shimmer" className="h-9 w-28" />
          </div>
        </div>

        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex flex-col gap-4 xl:flex-row">
              <Skeleton variant="shimmer" className="h-10 flex-1" />
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
                {[...Array(5)].map((_, index) => (
                  <Skeleton key={index} variant="shimmer" className="h-10 w-[170px]" />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  {["Time", "Actor", "Module", "Event", "Target", "Change", "Outcome", "Source / Network", "Evidence"].map((header) => (
                    <TableHead key={header}>{header}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(7)].map((_, index) => (
                  <TableRow key={index}>
                    {[...Array(9)].map((__, cellIndex) => (
                      <TableCell key={cellIndex}>
                        <Skeleton variant="shimmer" className="h-9 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-medium text-foreground">Audit Trail</h1>
          <p className="max-w-3xl text-muted-foreground">
            Enterprise audit history across live policy events and platform activity, with searchable evidence for each recorded action.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchActivityLogs(true)}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
          <Button onClick={exportToCSV} disabled={filteredEvents.length === 0} className="gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <AuditMetric icon={Database} label="Events" value={filteredEvents.length.toLocaleString()} />
        <AuditMetric icon={Layers3} label="Modules" value={filterOptions.modules.length.toLocaleString()} />
        <AuditMetric icon={ShieldCheck} label="Successful" value={filteredEvents.filter((event) => event.outcome === "Success").length.toLocaleString()} />
        <AuditMetric icon={Calendar} label="Window" value={dateRange?.from && dateRange?.to ? `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}` : "All time"} />
      </div>

      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 xl:flex-row">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search actor, event, target, IP, or evidence ID..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
              <Select value={moduleFilter} onValueChange={setModuleFilter}>
                <SelectTrigger className="w-full lg:w-[170px]">
                  <SelectValue placeholder="Module" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Modules</SelectItem>
                  {filterOptions.modules.map((module) => (
                    <SelectItem key={module} value={module}>
                      {module}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full lg:w-[170px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {filterOptions.categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
                <SelectTrigger className="w-full lg:w-[150px]">
                  <SelectValue placeholder="Outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Outcomes</SelectItem>
                  {filterOptions.outcomes.map((outcome) => (
                    <SelectItem key={outcome} value={outcome}>
                      {outcome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-full lg:w-[180px]">
                  <SelectValue placeholder="Actor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actors</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <DatePickerWithRange date={dateRange} onDateChange={setDateRange} className="w-full lg:w-[250px]" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardContent className="p-0">
          <AppDataTable
            columns={auditColumns}
            data={filteredEvents}
            emptyMessage="No audit trail entries found."
            getRowId={(event) => event.id}
            minWidthClassName="min-w-[1420px]"
            onRowClick={setSelectedEvent}
          />
        </CardContent>
      </Card>

      {filteredEvents.length === 0 && !loading && (
        <div className="py-12 text-center">
          <Clock className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-2 text-lg font-semibold text-muted-foreground">No audit trail entries found</h3>
          <p className="text-sm text-muted-foreground">Try adjusting your search, module, actor, or date filters.</p>
        </div>
      )}

      <AuditEventSheet event={selectedEvent} onOpenChange={(open) => !open && setSelectedEvent(null)} />
    </div>
  );
};

type AuditMetricProps = {
  icon: typeof Activity;
  label: string;
  value: string;
};

function AuditMetric({ icon: Icon, label, value }: AuditMetricProps) {
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm text-muted-foreground">{label}</div>
          <div className="mt-1 text-2xl font-semibold text-foreground">{value}</div>
        </div>
        <div className="rounded-md bg-primary/10 p-2 text-primary">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function AuditEventSheet({
  event,
  onOpenChange,
}: {
  event: AuditEvent | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={Boolean(event)} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-hidden sm:max-w-2xl">
        {event && (
          <ScrollArea className="h-full pr-4">
            <SheetHeader className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={cn(MODULE_STYLES[event.module])}>
                  {event.module}
                </Badge>
                <Badge variant="outline" className={cn(OUTCOME_STYLES[event.outcome])}>
                  {event.outcome}
                </Badge>
              </div>
              <SheetTitle>{event.eventName}</SheetTitle>
              <SheetDescription>{event.summary}</SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              <AuditDetailSection
                title="Event Record"
                rows={[
                  ["Timestamp", formatAuditTimestamp(event.timestamp)],
                  ["Event ID", event.id],
                  ["Category", event.eventCategory],
                  ["Source", event.source],
                ]}
              />

              <AuditDetailSection
                title="Actor"
                rows={[
                  ["Name", event.actorName],
                  ["Email", event.actorEmail || "N/A"],
                  ["Type", event.actorType],
                ]}
              />

              <AuditDetailSection
                title="Target"
                rows={[
                  ["Type", event.targetType],
                  ["Name", event.targetName],
                  ["Resource ID", event.targetId || "N/A"],
                ]}
              />

              <div>
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  Change Details
                </h3>
                <div className="rounded-lg border">
                  {event.fieldChanges.length > 0 ? (
                    event.fieldChanges.map((change, index) => (
                      <div key={`${change.field}-${index}`} className="grid gap-2 border-b p-3 last:border-b-0 sm:grid-cols-[140px_1fr]">
                        <div className="text-sm font-medium">{change.field}</div>
                        <div className="text-sm text-muted-foreground">
                          <span>{change.from}</span>
                          <span className="px-2 text-foreground">{"->"}</span>
                          <span>{change.to}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-sm text-muted-foreground">No field-level change was captured for this event.</div>
                  )}
                </div>
              </div>

              <AuditDetailSection
                title="Source & Network"
                rows={[
                  ["IP Address", event.ipAddress || "N/A"],
                  ["Device", event.device || "N/A"],
                  ["User Agent", event.raw.user_agent || "N/A"],
                ]}
              />

              <div>
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                  <Fingerprint className="h-4 w-4 text-muted-foreground" />
                  Raw Evidence Payload
                </h3>
                <pre className="max-h-80 overflow-auto rounded-lg border bg-muted/50 p-4 text-xs leading-relaxed">
                  {JSON.stringify(event.raw, null, 2)}
                </pre>
              </div>
            </div>
          </ScrollArea>
        )}
      </SheetContent>
    </Sheet>
  );
}

function AuditDetailSection({ title, rows }: { title: string; rows: [string, string][] }) {
  return (
    <div>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <Info className="h-4 w-4 text-muted-foreground" />
        {title}
      </h3>
      <div className="rounded-lg border">
        {rows.map(([label, value], index) => (
          <div key={label}>
            <div className="grid gap-2 p-3 sm:grid-cols-[140px_1fr]">
              <div className="text-sm font-medium">{label}</div>
              <div className="break-words text-sm text-muted-foreground">{value}</div>
            </div>
            {index < rows.length - 1 && <Separator />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ActivityLog;
